import * as Company from '@web/contracts/Company';
import React from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
type AvailableTabs = Company.AvailableTabs;

export interface CompanyState {
  selectedId: string | null;
  selectedName: string | null;
  selectedTab: AvailableTabs;
  selectedCountry: AvailableCountries | null;
  defaultCountries: AvailableCountries[] | null;
  peopleTableRef: React.RefObject<HTMLDivElement | null> | null;
}

export interface CompanyAction {
  load: (id: string, name: string) => void;
  clear: () => void;
  setTab: (tab: AvailableTabs) => void;
  setSelectedCountry: (country: AvailableCountries) => void;
  setDefaultCountries: (defaultCountries: AvailableCountries[]) => void;
  setPeopleTableRef: (ref: React.RefObject<HTMLDivElement | null>) => void;
  scrollToPeopleTable: () => void;
}

/**
 * Store to maintain the currently selected company id
 */
const useCompany = create<CompanyState & CompanyAction>()(
  devtools(
    immer((set, get) => ({
      selectedId: null,
      selectedName: null,
      selectedTab: 'profile',
      selectedCountry: null,
      defaultCountries: null,
      peopleTableRef: null,
      load: (selectedId: string, selectedName: string) =>
        set(
          (state) => {
            state.selectedId = selectedId;
            state.selectedName = selectedName;
          },
          false,
          { type: 'LOAD' },
        ),
      clear: () => set({ selectedId: null }, false, { type: 'CLEAR_SELECTION' }),
      setTab: (selectedTab: AvailableTabs) => set({ selectedTab }, false, { type: 'SET_TAB' }),
      setSelectedCountry: (selectedCountry: AvailableCountries) =>
        set({ selectedCountry }, false, { type: 'SET_SELECTED_COUNTRY' }),
      setDefaultCountries: (defaultCountries: AvailableCountries[]) =>
        set({ defaultCountries }, false, { type: 'SET_DEFAULT_COUNTRIES' }),
      setPeopleTableRef: (peopleTableRef: React.RefObject<HTMLDivElement | null>) =>
        set({ peopleTableRef }, false, { type: 'SET_PEOPLE_TABLE_REF' }),
      scrollToPeopleTable: () => {
        const { peopleTableRef } = get();
        if (peopleTableRef?.current) {
          peopleTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
    })),
    {
      store: 'Company',
    },
  ),
);

export { useCompany };
